import type { IPagingFilter } from "@frui.ts/data";
import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import type { EntityNames } from "config";
import type RefundTransferPaymentResponse from "entities/refundTransferPaymentResponse";
import type TransferRefundDetailResponse from "entities/transferRefundDetailResponse";
import TransferRefundPaymentTemplate from "entities/transferRefundPaymentTemplate";
import TransferRefundResponse from "entities/transferRefundResponse";
import type TransferType from "entities/transferType";
import { computed } from "mobx";
import type RefundRepository from "repositories/refundRepository";
import type { UpdatedEvent } from "services/eventBus";
import type EventBus from "services/eventBus";
import {
  domesticTransferRefundUpdated,
  foreignTransferRefundUpdated,
  SEPATransferRefundUpdated,
  transferRefundPaymentTypeChanged,
} from "services/eventBus";
import type EnumService from "services/enumService";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";
import type RefundsApprovalService from "services/refundsApprovalService";
import type AppRouter from "services/router";
import type SecurityService from "services/securityService";
import { getEntityName } from "utils/entityName";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import type AlertsViewModel from "../../../alertsViewModel";
import EditableRefundDetailViewModel from "../detail/editableRefundDetailViewModel";
import TransferPaymentType from "entities/transferPaymentType";
import FeeType from "entities/feeType";
import type PriceListService from "services/priceListService";
import { emptyValue } from "utils/emptyValue";

export default abstract class TransferRefundDetailViewModel extends EditableRefundDetailViewModel<
  TransferRefundDetailResponse,
  TransferRefundResponse
> {
  readonly modelName: EntityNames;

  @computed
  get showSEPAWarning() {
    const countryCode = this.item.refundPayment.counterpartyBank.countryCode ?? "";
    const isSepaMember = this.enumService.values("countries").find(item => item.code === countryCode)?.isSepaMember ?? false;

    return this.item.refundPayment.transferType === TransferPaymentType.Foreign && isSepaMember && this.isPaymentTypeEditable;
  }

  constructor(
    protected itemId: string,
    protected pagingFilter: IPagingFilter,
    protected transferType: TransferPaymentType,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected approvalService: RefundsApprovalService,
    protected repository: RefundRepository,
    protected optionsService: OptionsService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    protected enumService: EnumService,
    protected priceListService: PriceListService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(
      itemId,
      pagingFilter,
      localizationService,
      appRouter,
      approvalService,
      repository,
      optionsService,
      securityService,
      eventBus,
      countrySelectViewModelFactory,
      enumService,
      priceListService,

      alertsViewModel
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.modelName = getEntityName(TransferRefundResponse)!;
  }

  @watchBusy
  public fetchData() {
    const { limit, offset } = this.pagingFilter;

    return this.repository.getTransferRefunds({ limit, offset }, { transferType: this.transferType });
  }

  protected fetchDetail(id: string): Promise<TransferRefundDetailResponse> {
    return this.repository.getTransferRefund(id);
  }

  protected updatePayment(id: string, data: RefundTransferPaymentResponse): Promise<any> {
    return this.repository.updateTransferRefundPayment(id, data);
  }

  @bound
  @watchBusy
  onRefundTypeChanged() {
    this.eventBus.publish(
      transferRefundPaymentTypeChanged({
        newTransferType: this.item.refundPayment.transferType || TransferPaymentType.Unspecified,
        originalTransferType: this.originalPaymentType,
        refundId: this.itemId,
      })
    );
    this.requestClose();
  }

  @bound
  protected async onRefundSaved() {
    if (this.originalPaymentType !== this.item.refundPayment.transferType) {
      return this.onRefundTypeChanged();
    }

    switch (this.item.refundPayment.transferType) {
      case TransferPaymentType.Sepa:
        this.eventBus.publish(SEPATransferRefundUpdated());
        break;
      case TransferPaymentType.Domestic:
        this.eventBus.publish(domesticTransferRefundUpdated());
        break;
      case TransferPaymentType.Foreign:
        this.eventBus.publish(foreignTransferRefundUpdated());
        break;
    }

    this.eventBus.publish<UpdatedEvent<RefundTransferPaymentResponse>>({
      type: `transferRefundPayment.updated`,
      payload: { transferRefundPayment: this.item.refundPayment },
    });

    await this.loadDetail();
  }

  @computed
  get chargeRefundFee() {
    return (this.item.fees ?? []).find(fee => fee.feeType === FeeType.ChargeRefundFee)?.amount ?? 0;
  }

  @computed
  get bankFees() {
    return (this.item.fees ?? []).reduce((sum: number, fee) => {
      if (fee.feeType === FeeType.ForeignPaymentCost || fee.feeType === FeeType.ForeignPaymentToSepaCountriesCost) {
        sum += fee.amount;
      }
      return sum;
    }, 0);
  }

  translateRefundType(type: TransferType) {
    return this.localizationService.translateEnum("transferType", type);
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService.entityOptions(TransferRefundPaymentTemplate).find(option => option.id === refundPaymentTemplateId)
        ?.label ?? emptyValue
    );
  }
}
